import React, { useEffect } from 'react';

import SEO from '../SEO';import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { CheckCircle, ArrowRight, Shield, AlertCircle, Droplets, Wind, Flame, Factory } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';
import { useNavigate } from 'react-router-dom';

const ActiveCorrosionControl = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const corrosionTypes = [
    {
      title: 'Atmospheric Corrosion',
      description: 'Protection against moisture, salt spray, and environmental exposure in outdoor and coastal environments.',
      icon: Wind,
      solutions: ['Barrier coatings', 'Zinc-rich primers', 'Moisture barriers']
    },
    {
      title: 'Chemical Corrosion',
      description: 'Defense against acids, alkalis, solvents, and aggressive chemical environments.',
      icon: Droplets,
      solutions: ['Chemical-resistant linings', 'Epoxy novolacs', 'Vinyl esters']
    },
    {
      title: 'High-Temperature Corrosion',
      description: 'Protection in elevated temperature environments where traditional coatings fail.',
      icon: Flame,
      solutions: ['Ceramic coatings', 'High-temp primers', 'Thermal barriers']
    },
    {
      title: 'Industrial Corrosion',
      description: 'Comprehensive protection for processing equipment, storage, and infrastructure.',
      icon: Factory,
      solutions: ['Multi-layer systems', 'Sacrificial anodes', 'Cathodic protection']
    }
  ];

  const protectionStrategies = [
    { title: 'Surface Preparation', description: 'Proper cleaning and profiling for maximum coating adhesion' },
    { title: 'Barrier Protection', description: 'Physical barriers that isolate metal from corrosive environments' },
    { title: 'Inhibitive Coatings', description: 'Active corrosion inhibitors that neutralize corrosive agents' },
    { title: 'Cathodic Protection', description: 'Electrochemical protection using sacrificial materials' },
    { title: 'Environmental Control', description: 'Managing humidity, temperature, and chemical exposure' },
    { title: 'Regular Monitoring', description: 'Ongoing inspection to catch corrosion before it spreads' }
  ];

  const benefits = [
    'Prevent structural failures',
    'Extend equipment service life',
    'Reduce maintenance costs',
    'Maintain safety compliance',
    'Protect asset value',
    'Minimize production disruptions'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title="Active Corrosion Control" description="Active corrosion control programs for industrial facilities. Preventive coatings and monitoring." path="/maintenance/active-corrosion-control" />
      
      <PageHero
        badge="SMART Program"
        title="Active Corrosion Control"
        description="Proactive protection strategies that stop corrosion before it causes damage. Our multi-layered approach protects your assets in even the most aggressive environments."
        backgroundKey="active-corrosion-control"
        buttonText="Get Corrosion Assessment"
      />

      {/* Corrosion Types */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Corrosion Challenges We Solve</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {corrosionTypes.map((type, index) => {
              const IconComponent = type.icon;
              return (
                <Card key={index} className="border-l-4 border-l-red-600">
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <IconComponent className="text-red-600 mr-3" size={28} />
                      {type.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-4">{type.description}</p>
                    <div className="bg-slate-50 p-3 rounded">
                      <p className="text-sm font-medium text-slate-700 mb-2">Our Solutions:</p>
                      <ul className="text-sm text-gray-600 space-y-1">
                        {type.solutions.map((solution, idx) => (
                          <li key={idx} className="flex items-center">
                            <Shield className="text-green-700 mr-2" size={14} />
                            {solution}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Protection Strategies */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Our Protection Strategies</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {protectionStrategies.map((strategy, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-sm">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <span className="text-green-700 font-bold">{index + 1}</span>
                </div>
                <h3 className="font-bold text-slate-900 mb-2">{strategy.title}</h3>
                <p className="text-gray-600 text-sm">{strategy.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Benefits of Active Corrosion Control</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start p-4 bg-green-50 rounded-lg">
                <CheckCircle className="text-green-700 mr-3 mt-1 flex-shrink-0" size={20} />
                <p className="text-gray-700">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <AlertCircle className="mx-auto mb-4" size={48} />
          <h2 className="text-3xl font-bold mb-4">Corrosion Never Sleeps</h2>
          <p className="text-blue-100 mb-8">
            Every day without protection is another day of damage. Start your corrosion control program today.
          </p>
          <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => navigate('/#contact')}>
            Schedule Corrosion Survey <ArrowRight className="ml-2" size={20} />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ActiveCorrosionControl;
